import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { TESTIDS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | already | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email, language: lang });
      setStatus("success");
      setMessage(pick(t.footer.newsletter.success, lang));
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus("already");
        setMessage(pick(t.footer.newsletter.already, lang));
      } else {
        setStatus("error");
        setMessage(pick(t.contact.form.error, lang));
      }
    }
  };

  return (
    <footer className="relative bg-depth text-canvas overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />
      <div className="peony-watermark hidden md:block" style={{ width: "60rem", height: "60rem", right: "-18rem", bottom: "-22rem", opacity: 0.18 }} />

      <div className="relative px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-12">
        {/* Top CTA strip */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-24">
          <div className="lg:col-span-7">
            <p className="eyebrow-light">{pick(t.home.footer_cta.whisper, lang)}</p>
            <h2 className="mt-6 font-display font-light text-[clamp(2.4rem,5.5vw,5rem)] leading-[1.02] tracking-tight">
              {pick(t.home.footer_cta.headline, lang)}
            </h2>
            <div className="mt-10">
              <Link to="/kontakt" className="btn-light">
                {pick(t.home.footer_cta.cta, lang)}
                <span aria-hidden>↗</span>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-12 lg:border-l border-warmth/20">
            <p className="eyebrow-light">{pick(t.footer.newsletter.label, lang)}</p>
            <p className="mt-6 font-display italic text-warmth/90 text-xl md:text-2xl leading-snug">
              {pick(t.footer.newsletter.blurb, lang)}
            </p>
            <form onSubmit={submit} className="mt-8 flex items-end gap-4 border-b border-warmth/30 pb-3">
              <input
                type="email"
                required
                data-testid={TESTIDS.newsletterInput}
                placeholder={pick(t.footer.newsletter.placeholder, lang)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent outline-none border-none w-full text-canvas placeholder:text-warmth/40 font-light text-base py-2"
              />
              <button
                type="submit"
                data-testid={TESTIDS.newsletterSubmit}
                disabled={status === "sending"}
                className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-canvas hover:text-warmth transition-colors py-2 disabled:opacity-50"
              >
                {status === "sending" ? "…" : pick(t.footer.newsletter.submit, lang)} ↗
              </button>
            </form>
            {status !== "idle" && status !== "sending" && (
              <p
                data-testid={TESTIDS.newsletterSuccess}
                className={`mt-4 text-sm font-light ${status === "error" ? "text-signal" : "text-warmth"}`}
              >
                {message}
              </p>
            )}
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 pt-12 border-t border-warmth/20">
          {/* Studio */}
          <div className="col-span-2 md:col-span-1">
            <p className="eyebrow-light">{pick(t.footer.columns.studio, lang)}</p>
            <p className="font-display text-3xl mt-6 leading-tight">
              patrice<span className="text-signal">.</span>studio
            </p>
            <p className="mt-4 text-warmth/80 text-sm font-light leading-relaxed">
              {pick(t.home.lede, lang)}
            </p>
          </div>

          {/* Universe */}
          <div>
            <p className="eyebrow-light">{pick(t.footer.columns.universe, lang)}</p>
            <ul className="mt-6 space-y-3 text-sm font-light">
              <li><Link to="/projekte" className="hover:text-warmth transition-colors">{pick(t.nav.projects, lang)}</Link></li>
              <li><Link to="/ueber-mich" className="hover:text-warmth transition-colors">{pick(t.nav.about, lang)}</Link></li>
              <li><Link to="/journal" className="hover:text-warmth transition-colors">{pick(t.nav.journal, lang)}</Link></li>
              <li><Link to="/kontakt" className="hover:text-warmth transition-colors">{pick(t.nav.contact, lang)}</Link></li>
            </ul>
          </div>

          {/* Quietly */}
          <div>
            <p className="eyebrow-light">{pick(t.footer.columns.whisper, lang)}</p>
            <ul className="mt-6 space-y-3 text-sm font-light">
              {t.contact.socials
                .filter((s) => !/sichtbar/i.test(s.label))
                .map((s) => (
                  <li key={s.label}>
                    <a href={s.href} target="_blank" rel="noopener noreferrer" className="hover:text-warmth transition-colors">
                      {s.label} <span className="text-warmth/60">— {s.handle}</span>
                    </a>
                  </li>
                ))}
              <li>
                <Link to="/sgb" className="hover:text-warmth transition-colors">
                  Sichtbar Gut Bewertet <span className="text-warmth/60">— Spezialisierung</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className="eyebrow-light">Legal</p>
            <ul className="mt-6 space-y-3 text-sm font-light">
              <li><Link to="/impressum" className="hover:text-warmth transition-colors">{pick(t.footer.legal.imprint, lang)}</Link></li>
              <li><Link to="/datenschutz" className="hover:text-warmth transition-colors">{pick(t.footer.legal.privacy, lang)}</Link></li>
              <li><Link to="/agb" className="hover:text-warmth transition-colors">{pick(t.footer.legal.terms, lang)}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-warmth/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-warmth/70 text-xs font-mono tracking-[0.28em] uppercase">
            {pick(t.footer.legal.copyright, lang)}
          </p>
          <p className="text-warmth/60 text-xs font-light italic font-display">
            Inspired by peonies — present without proving.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
